import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useScrollToTopOnChange } from '../utils/scroll';
import Sortable from 'sortablejs';
import { FlaskConical, Search, X, GripVertical, Trash2, Plus, Check, Edit2 } from 'lucide-react';
import { quickLabTestAPI } from '../services/api';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import { useToast, useConfirm } from './UIProvider';
import useDialog from '../hooks/useDialog';
import useKeyboardReorder from '../hooks/useKeyboardReorder';

// Configures the "Common tests" quick chips shown in the prescription Treatment tab, scoped per
// speciality. The General scope is what every doctor sees; a speciality's own list is shown ahead of
// it. When nothing is configured anywhere, the prescription falls back to the legacy common-test
// catalog so the chips are never empty.

const ROW_GRID = 'grid grid-cols-[2.5rem_1.4fr_1.2fr_8rem_8rem_5rem] items-center';

const QuickLabTestsTab = ({
  specialityId,
  scopeLabel,
  libraryOpen = false,
  onLibraryClose,
  newTestOpen = false,
  onNewTestClose,
  refreshKey = 0,
}) => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Picking tests happens in a modal opened from the page header, mirroring the instruction tabs'
  // "Add from Library" rather than an inline search box unique to this tab.
  const pickerOpen = libraryOpen;
  const [selection, setSelection] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState('all'); // all | available | added
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [adding, setAdding] = useState(false);
  // The entire catalog, loaded once and filtered in the browser: the picker offers every test
  // there is, whatever the speciality or category. A test carries its own category along.
  const [catalog, setCatalog] = useState(null); // null = loading
  const [categories, setCategories] = useState([]);
  const [showNewTest, setShowNewTest] = useState(false);
  const [newTest, setNewTest] = useState({ testName: '', testNameUrdu: '', category: 'OTHER' });
  const [savingNewTest, setSavingNewTest] = useState(false);
  // Editing a catalogue entry in place, the same way the instruction library does it. A test is one
  // shared row, so this reaches every speciality that picked it.
  const [catalogEdit, setCatalogEdit] = useState(null); // { id, testName, testNameUrdu, category }
  const [catalogEditSaving, setCatalogEditSaving] = useState(false);
  // Status toggles and removals are staged, not saved, and the whole screenful commits as one
  // transaction. Same shape as the instruction tabs so the two screens behave identically.
  const [pendingActive, setPendingActive] = useState(() => new Map());
  const [pendingRemove, setPendingRemove] = useState(() => new Set());
  const [savingBatch, setSavingBatch] = useState(false);

  // The library modal's scrolling row list. Paging replaces the rows but not the scroller, so
  // without this the second page opens already scrolled to its last row.
  const itemsRef = useRef(null);
  const libraryListRef = useRef(null);
  const scopeRef = useRef(specialityId);
  const showToastRef = useRef(showToast);
  const sortableRef = useRef(null);
  const saveOrderRef = useRef(null);
  itemsRef.current = items;
  scopeRef.current = specialityId;
  showToastRef.current = showToast;
  saveOrderRef.current = (next) => {
    quickLabTestAPI
      .reorder(scopeRef.current, next.map((i) => i.id))
      .catch(async (err) => {
        showToastRef.current('error', err.message || 'Failed to save order');
        try { const fresh = await quickLabTestAPI.list(scopeRef.current); setItems((Array.isArray(fresh) ? fresh : []).filter((r) => r.isActive !== false)); } catch { /* ignore */ }
      });
  };

  const load = useCallback(async () => {
    setItems(null);
    try {
      const list = await quickLabTestAPI.list(specialityId);
      // Switching a test off is how it leaves this speciality's list, so a row that comes back
      // inactive is not shown here at all. The endpoint still returns both, but on this screen an
      // inactive row would be a dead entry: no doctor is served it, and the way to bring it back is
      // Add from Library, which offers it again and reactivates this very row.
      const active = (Array.isArray(list) ? list : []).filter((r) => r.isActive !== false);
      setItems(active);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load quick tests');
      setItems([]);
    }
  }, [specialityId]);

  useEffect(() => { load(); }, [load]);

  // Refresh is driven from the page header, the same button the instruction tabs use.
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return; }
    load();
  }, [refreshKey, load]);

  const loadCatalog = useCallback(async () => {
    try {
      const list = await quickLabTestAPI.catalog();
      setCatalog(Array.isArray(list) ? list : []);
    } catch (e) {
      setCatalog([]);
      showToastRef.current('error', e.message || 'Failed to load the test catalog');
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  useEffect(() => {
    quickLabTestAPI.categories()
      .then(list => setCategories(Array.isArray(list) ? list : []))
      .catch(() => setCategories([]));
  }, []);

  // Reset the picker's own state when the scope changes so a test can't be added to the wrong list
  // and stale filters don't carry across. The parent owns whether the modal is open.
  useEffect(() => {
    setQuery('');
    setShowNewTest(false);
    setSelection(new Set());
    setStatusFilter('all');
    setCategoryFilter('all');
    setCatalogEdit(null);
    // Staged edits belong to the list that was on screen, so switching speciality abandons them
    // rather than carrying them onto rows they were never made against.
    setPendingActive(new Map());
    setPendingRemove(new Set());
  }, [specialityId]);

  // Fresh filters each time the modal opens.
  useEffect(() => {
    if (!libraryOpen) return;
    setQuery('');
    setSelection(new Set());
    setStatusFilter('all');
    setCategoryFilter('all');
    setCatalogEdit(null);
  }, [libraryOpen]);

  const rowsContainerRef = useCallback((node) => {
    if (sortableRef.current) { sortableRef.current.destroy(); sortableRef.current = null; }
    if (!node) return;
    sortableRef.current = Sortable.create(node, {
      animation: 180,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
      forceFallback: true,
      fallbackTolerance: 4,
      filter: 'button',
      preventOnFilter: false,
      ghostClass: 'si-ghost',
      chosenClass: 'si-chosen',
      dragClass: 'si-drag',
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
        const current = itemsRef.current || [];
        const next = [...current];
        const [moved] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, moved);
        setItems(next);
        saveOrderRef.current(next);
      },
    });
  }, []);

  const handleProps = useKeyboardReorder({
    items: items || [],
    onMove: setItems,
    onCommit: (next) => saveOrderRef.current(next),
    getLabel: (row) => row.labTest?.testName || 'test',
  });

  const addedTestIds = useMemo(
    () => new Set((items || []).map((i) => i.labTest?.id).filter(Boolean)),
    [items]
  );

  // Filtering happens here, not on the server: the whole catalogue is browsable and the search box
  // narrows it by name or code. Tests already on this speciality's list stay visible but unpickable,
  // the same way the instruction library shows what a scope already holds.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (catalog || []).filter((t) => {
      const already = addedTestIds.has(t.id);
      if (statusFilter === 'available' && already) return false;
      if (statusFilter === 'added' && !already) return false;
      if (categoryFilter !== 'all' && (t.category || 'OTHER') !== categoryFilter) return false;
      if (!q) return true;
      return (t.testName || '').toLowerCase().includes(q)
        || (t.testCode || '').toLowerCase().includes(q);
    });
  }, [catalog, query, statusFilter, categoryFilter, addedTestIds]);

  const catalogCategories = useMemo(
    () => [...new Set((catalog || []).map((t) => t.category || 'OTHER'))].sort((a, b) => a.localeCompare(b)),
    [catalog]
  );
  const availableCount = (catalog || []).filter((t) => !addedTestIds.has(t.id)).length;

  // A new search, filter, speciality or opening of the picker starts again at the first page.
  const paging = usePagedRows(results, {
    id: 'quickLabTests.library',
    initialSize: 10,
    resetOn: [query, statusFilter, categoryFilter, specialityId, libraryOpen],
  });
  // Anything that swaps the visible rows returns the list to the top.
  useScrollToTopOnChange(libraryListRef, [paging.page, paging.pageSize, query, statusFilter, categoryFilter]);
  const pagedResults = paging.pageRows;

  const closePicker = useCallback(() => {
    setShowNewTest(false);
    if (onLibraryClose) onLibraryClose();
  }, [onLibraryClose]);

  // Ticks are clicks, which the hook cannot see, and the filters are not changes.
  const pickerDialogRef = useDialog(closePicker, { isDirty: () => selection.size > 0 });
  const newTestDialogRef = useDialog(() => { if (!savingNewTest) onNewTestClose(); });

  const togglePick = (id) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // No batch endpoint exists, so the picks are added one call at a time and reported together —
  // the admin sees one result line, not one toast per test.
  const handleAddSelected = async () => {
    const ids = [...selection];
    if (ids.length === 0) return;
    setAdding(true);
    let added = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await quickLabTestAPI.add(specialityId, id);
        added += 1;
      } catch {
        failed += 1;
      }
    }
    setAdding(false);
    setSelection(new Set());
    if (added > 0) showToast('success', `${added} test${added === 1 ? '' : 's'} added to ${scopeLabel}`);
    if (failed > 0) showToast('error', `${failed} test${failed === 1 ? '' : 's'} could not be added`);
    await load();
    closePicker();
  };

  // Creates the test in the shared catalog and puts it straight on this list. From then on it is
  // searchable here like any other test, for every speciality. Reached two ways — the Add Test
  // button in the page header, and the shortcut at the foot of the library when a search finds
  // nothing — but it is one form and one save path, so the two can't drift.
  const handleCreateTest = async () => {
    const name = newTest.testName.trim();
    if (!name) {
      showToast('error', 'Enter the test name first');
      return;
    }
    setSavingNewTest(true);
    try {
      await quickLabTestAPI.createTest(specialityId, {
        testName: name,
        testNameUrdu: newTest.testNameUrdu.trim(),
        category: newTest.category,
      });
      showToast('success', `${name} added to ${scopeLabel}`);
      setNewTest({ testName: '', testNameUrdu: '', category: 'OTHER' });
      setShowNewTest(false);
      setQuery('');
      if (onNewTestClose) onNewTestClose();
      await Promise.all([load(), loadCatalog()]);
    } catch (err) {
      showToast('error', err.message || 'Failed to add the test');
    } finally {
      setSavingNewTest(false);
    }
  };

  // The name/Urdu/category fields, shared by the header's Add Test modal and the library shortcut.
  const newTestFields = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Test name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          autoFocus
          value={newTest.testName}
          onChange={(e) => setNewTest(prev => ({ ...prev, testName: e.target.value }))}
          placeholder="e.g. Anti Mullerian Hormone"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Urdu name</label>
        <input
          type="text"
          dir="rtl"
          value={newTest.testNameUrdu}
          onChange={(e) => setNewTest(prev => ({ ...prev, testNameUrdu: e.target.value }))}
          placeholder="اردو نام"
          className="w-full px-3 py-2 text-lg urdu-text text-right border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
        <Select
          value={newTest.category}
          onChange={(v) => setNewTest(prev => ({ ...prev, category: v }))}
          options={categories.map(c => ({ value: c.value, label: c.label }))}
          buttonClassName="px-3 py-2"
        />
      </div>
    </div>
  );

  // Catalogue-level edit. One test is one shared row, so renaming it changes it for every speciality
  // that picked it. Prescriptions already written keep the name they were written under.
  const saveCatalogEdit = async () => {
    const name = (catalogEdit.testName || '').trim();
    if (!name) {
      showToast('error', 'Test name is required');
      return;
    }
    setCatalogEditSaving(true);
    try {
      const res = await quickLabTestAPI.updateCatalogTest(catalogEdit.id, {
        testName: name,
        testNameUrdu: (catalogEdit.testNameUrdu || '').trim(),
        category: catalogEdit.category,
      });
      setCatalogEdit(null);
      await Promise.all([loadCatalog(), load()]);
      const n = res?.updated ?? 0;
      showToast('success', n > 1 ? `Updated in all ${n} specialities using it` : 'Test updated');
    } catch (e) {
      showToast('error', e.message || 'Failed to update the test');
    } finally {
      setCatalogEditSaving(false);
    }
  };

  const deleteCatalogEntry = async (entry) => {
    const where = (entry.scopes || []).length
      ? ` It is in use by ${entry.scopes.join(', ')}.`
      : '';
    const ok = await confirm({
      title: 'Delete from the catalogue?',
      message: `"${entry.testName}" will be removed from the catalogue and from every speciality that uses it.${where} It can no longer be prescribed. Prescriptions already written are not affected.`,
      confirmText: 'Delete everywhere',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const res = await quickLabTestAPI.deleteCatalogTest(entry.id);
      await Promise.all([loadCatalog(), load()]);
      const n = res?.deleted ?? 0;
      showToast('success', n > 1 ? `Removed from all ${n} specialities` : 'Test deleted');
    } catch (e) {
      showToast('error', e.message || 'Failed to delete the test');
    }
  };

  // What a row's status will be once the pending changes are saved.
  const effectiveActive = (row) =>
    pendingActive.has(row.id) ? pendingActive.get(row.id) : row.isActive !== false;

  // Staged, not saved. Toggling back to where it started drops the entry rather than recording a
  // no-op, so the unsaved count only ever reflects real changes.
  const handleToggleActive = (row) => {
    setPendingActive((prev) => {
      const next = new Map(prev);
      const target = !effectiveActive(row);
      if (target === (row.isActive !== false)) next.delete(row.id);
      else next.set(row.id, target);
      return next;
    });
  };

  const toggleRemove = (row) => {
    setPendingRemove((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  const discardChanges = () => {
    setPendingActive(new Map());
    setPendingRemove(new Set());
  };

  const pendingCount = pendingActive.size + pendingRemove.size;

  const saveBatch = async () => {
    const removeIds = [...pendingRemove];
    // A staged toggle on a row that is also being removed is moot; the removal wins.
    const activate = [...pendingActive.entries()]
      .filter(([id, on]) => on && !pendingRemove.has(id))
      .map(([id]) => id);
    const deactivate = [...pendingActive.entries()]
      .filter(([id, on]) => !on && !pendingRemove.has(id))
      .map(([id]) => id);

    if (removeIds.length > 0) {
      const names = (items || [])
        .filter((row) => pendingRemove.has(row.id))
        .map((row) => `"${row.labTest?.testName}"`);
      const ok = await confirm({
        title: removeIds.length === 1 ? 'Remove from this list?' : `Remove ${removeIds.length} from this list?`,
        message: `${names.join(', ')} will be taken off ${scopeLabel}'s list. The test stays in the catalogue, so it can be added back at any time, and prescriptions already written are unaffected.`,
        confirmText: 'Save changes',
        tone: 'danger',
      });
      if (!ok) return;
    }

    setSavingBatch(true);
    try {
      const res = await quickLabTestAPI.batch(specialityId, { activate, deactivate, remove: removeIds });
      discardChanges();
      await load();
      const parts = [];
      if (res?.activated) parts.push(`${res.activated} activated`);
      if (res?.deactivated) parts.push(`${res.deactivated} removed from this list`);
      if (res?.removed) parts.push(`${res.removed} deleted`);
      showToast('success', parts.length ? parts.join(', ') : 'Changes saved');
    } catch (err) {
      showToast('error', err.message || 'Failed to save changes');
    } finally {
      setSavingBatch(false);
    }
  };

  return (
    <div className="space-y-4">

      {/* Add from Library — same shape as the instruction tabs' library: search, the two in-result
          filters, a paged list, and multi-select with one batch add. Creating a test the catalogue
          has never carried lives at the foot of the list, where the search that failed to find it
          just was. */}
      {pickerOpen && (
        <div ref={pickerDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) closePicker(); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add from Library</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Tests from the shared catalogue. The ones you pick become quick chips for <span className="font-medium text-gray-700">{scopeLabel}</span>.
                </p>
              </div>
              <button aria-label="Close" onClick={closePicker} className="p-2 hover:bg-gray-100 rounded-lg flex-none"><X className="w-5 h-5" /></button>
            </div>

            <div className="px-6 pt-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the test catalogue"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[11rem]">
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: 'all', label: `All (${(catalog || []).length})` },
                      { value: 'available', label: `Not added yet (${availableCount})` },
                      { value: 'added', label: `Already added (${(catalog || []).length - availableCount})` },
                    ]}
                    buttonClassName="px-3 py-2"
                  />
                </div>
                {catalogCategories.length > 1 && (
                  <div className="min-w-[11rem]">
                    <Select
                      value={categoryFilter}
                      onChange={setCategoryFilter}
                      options={[
                        { value: 'all', label: 'Any category' },
                        ...catalogCategories.map((c) => ({ value: c, label: c })),
                      ]}
                      buttonClassName="px-3 py-2"
                    />
                  </div>
                )}
                {/* With nothing to page, the count still says how much of the catalogue the filters left. */}
                {catalog !== null && results.length === 0 && (
                  <span className="text-sm text-gray-500 ml-auto">0 of {catalog.length} shown</span>
                )}
              </div>
            </div>

            {catalog !== null && (
              <TablePager paging={paging} noun="test" filteredFrom={catalog.length} className="px-6 mt-3 border-t" rounded={false} />
            )}

            <div ref={libraryListRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {catalog === null ? (
                <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
              ) : results.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm">
                  {(catalog || []).length === 0
                    ? 'The test catalogue is empty.'
                    : 'No test in the catalogue matches that. Add it below.'}
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                  {pagedResults.map((t) => {
                    const already = addedTestIds.has(t.id);
                    const picked = selection.has(t.id);
                    const isEditing = catalogEdit?.id === t.id;

                    if (isEditing) {
                      return (
                        <div key={t.id} className="px-4 py-3 bg-emerald-50/60 border-l-2 border-emerald-500">
                          <p className="text-[11px] text-gray-500 mb-2">
                            Editing changes this test everywhere it is used
                            {(t.scopes || []).length > 0 && `, including ${t.scopes.join(', ')}`}.
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <input
                              autoFocus
                              value={catalogEdit.testName}
                              onChange={(e) => setCatalogEdit({ ...catalogEdit, testName: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                              placeholder="Test name"
                            />
                            <input
                              value={catalogEdit.testNameUrdu}
                              onChange={(e) => setCatalogEdit({ ...catalogEdit, testNameUrdu: e.target.value })}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-lg urdu-text text-right outline-none focus:ring-2 focus:ring-emerald-500"
                              dir="rtl"
                              placeholder="اردو نام"
                            />
                            <Select
                              value={catalogEdit.category}
                              onChange={(v) => setCatalogEdit({ ...catalogEdit, category: v })}
                              options={categories.map(c => ({ value: c.value, label: c.label }))}
                              buttonClassName="px-3 py-2"
                            />
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              type="button"
                              onClick={saveCatalogEdit}
                              disabled={catalogEditSaving}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {catalogEditSaving ? 'Saving' : 'Save everywhere'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setCatalogEdit(null)}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={t.id}
                        className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                          already ? 'bg-gray-50' : picked ? 'bg-emerald-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        {/* The pick target is its own button so the edit/delete controls can sit
                            beside it — a button cannot legally contain other buttons. */}
                        <button
                          type="button"
                          disabled={already}
                          onClick={() => togglePick(t.id)}
                          className={`flex items-start gap-3 text-left min-w-0 flex-1 ${already ? 'cursor-not-allowed' : ''}`}
                        >
                          <span className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-none ${
                            already ? 'bg-gray-200 border-gray-200' : picked ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300 bg-white'
                          }`}>
                            {(picked || already) && <Check className={`w-3.5 h-3.5 ${already ? 'text-gray-500' : 'text-white'}`} />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block text-sm ${already ? 'text-gray-500' : 'text-gray-900'}`}>{t.testName}</span>
                            <span className="block text-[11px] text-gray-400 mt-0.5">
                              {t.category || 'OTHER'}
                              {(t.scopes || []).length > 0 && ` · In use by ${t.scopes.join(', ')}`}
                              {already && <span className="ml-2 text-emerald-700 font-medium">Already a quick test</span>}
                            </span>
                          </span>
                        </button>
                        {t.testNameUrdu && (
                          <span className={`urdu-text text-lg text-right flex-none max-w-[35%] ${already ? 'text-gray-400' : 'text-gray-700'}`} dir="rtl">{t.testNameUrdu}</span>
                        )}
                        <span className="flex items-center gap-1 flex-none">
                          <button
                            type="button"
                            onClick={() => setCatalogEdit({
                              id: t.id,
                              testName: t.testName || '',
                              testNameUrdu: t.testNameUrdu || '',
                              category: t.category || 'OTHER',
                            })}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Edit everywhere it is used"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteCatalogEntry(t)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-rose-700 hover:bg-rose-50"
                            title="Delete from the catalogue and every speciality"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Manual entry — for a test the catalogue has never carried. It joins the shared
                  catalogue, so from the next search onward it is picked like any other test. */}
              <div className="border border-gray-200 rounded-xl bg-gray-50/60 px-4 py-3">
                {!showNewTest ? (
                  <button
                    onClick={() => {
                      setShowNewTest(true);
                      // Seed from the search text, but never wipe a name already typed here.
                      setNewTest(prev => ({ ...prev, testName: prev.testName || query.trim() }));
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Plus className="h-3.5 w-3.5" />Test not listed? Add it manually
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New test</div>
                    {newTestFields}
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[11px] text-gray-400">
                        Saved to the shared catalogue, so it can be picked here and prescribed from then on.
                      </p>
                      <div className="flex gap-2 flex-none">
                        <button
                          onClick={() => setShowNewTest(false)}
                          className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateTest}
                          disabled={savingNewTest || !newTest.testName.trim()}
                          className="px-4 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {savingNewTest ? 'Saving...' : 'Save & add'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {catalog !== null && (
              <TablePager paging={paging} noun="test" filteredFrom={catalog.length} position="bottom" className="px-6" rounded={false} />
            )}

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-500">
                {selection.size > 0 ? `${selection.size} selected` : 'Nothing selected yet'}
              </span>
              <div className="flex gap-3">
                <button onClick={closePicker} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
                <button
                  onClick={handleAddSelected}
                  disabled={adding || selection.size === 0}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />{adding ? 'Adding...' : `Add to ${scopeLabel}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Test — the counterpart to the instruction tabs' "Add Instruction". Creates a test the
          catalogue has never carried and puts it straight on this speciality's list. */}
      {newTestOpen && (
        <div ref={newTestDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget && !savingNewTest) onNewTestClose(); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Add Test</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  For a test the catalogue does not carry yet. It is saved to the shared catalogue and
                  added to <span className="font-medium text-gray-700">{scopeLabel}</span>.
                </p>
              </div>
              <button aria-label="Close" onClick={onNewTestClose} disabled={savingNewTest} className="p-2 hover:bg-gray-100 rounded-lg flex-none disabled:opacity-50"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">{newTestFields}</div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={onNewTestClose}
                disabled={savingNewTest}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTest}
                disabled={savingNewTest || !newTest.testName.trim()}
                className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />{savingNewTest ? 'Saving...' : `Add to ${scopeLabel}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-red-700">{error}</div>}

      {/* Configured list */}
      {items === null ? (
        <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
          <FlaskConical className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-500">
            {specialityId ? `No quick tests for ${scopeLabel}` : 'No quick tests yet'}
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Use Add from Library to pick from the catalogue, or Add Test for one it does not carry yet. Until a list is configured, prescriptions fall back to the common tests marked in the lab catalog.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-6 py-2.5 bg-emerald-50/60 border-b border-emerald-100 text-xs text-emerald-700">
            <GripVertical className="h-4 w-4 flex-shrink-0" />
            Drag rows, or use the arrow keys on a row's handle, to set the order these tests appear in the prescription.
          </div>
          <div className={`${ROW_GRID} bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider`}>
            <div className="px-2 py-3"><span className="sr-only">Reorder</span></div>
            <div className="px-6 py-3 text-left">Test</div>
            <div className="px-6 py-3 text-right">Urdu</div>
            <div className="px-4 py-3 text-left">Category</div>
            <div className="px-4 py-3 text-left">Status</div>
            <div className="px-4 py-3 text-right">Actions</div>
          </div>
          <div ref={rowsContainerRef}>
            {items.map((row, index) => {
              const active = effectiveActive(row);
              const removing = pendingRemove.has(row.id);
              const changed = pendingActive.has(row.id) || removing;
              return (
              <div
                key={row.id}
                className={`si-row ${ROW_GRID} border-b border-gray-100 last:border-b-0 cursor-grab ${
                  removing
                    ? 'bg-rose-50/70'
                    : changed
                      ? 'bg-amber-50/60'
                      : 'bg-white hover:bg-gray-50/60'
                } ${!active && !removing ? 'opacity-60' : ''}`}
              >
                <div className="px-2 py-3 flex justify-center">
                  <span {...handleProps(row, index)} className="inline-flex p-1 rounded text-gray-400 hover:text-gray-600 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><GripVertical className="h-4 w-4" /></span>
                </div>
                <div className={`px-6 py-3 text-sm ${removing ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                  {row.labTest?.testName}
                  {/* The catalogue flag is a different thing from the toggle beside it: this one
                      says the test is retired for every speciality, not just switched off here. */}
                  {row.labTest?.isActive === false && (
                    <span className="ml-2 text-xs text-amber-600">Inactive in catalog</span>
                  )}
                </div>
                <div className={`px-6 py-3 text-lg text-right urdu-text ${removing ? 'text-gray-400 line-through' : 'text-gray-800'}`} dir="rtl">{row.labTest?.testNameUrdu || ''}</div>
                <div className="px-4 py-3 text-xs text-gray-500">{row.labTest?.category || ''}</div>
                <div className="px-4 py-3">
                  {/* Status is staged, not saved - nothing reaches the server until Save changes. */}
                  {removing ? (
                    <span className="text-xs font-medium text-rose-700">Removing</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={active}
                        onClick={() => handleToggleActive(row)}
                        title={active
                          ? 'Active - switch off to take it off this list (it stays in the catalogue)'
                          : 'Switched off - it leaves this list when you save'}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${active ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                      <span className={`text-xs font-medium ${active ? 'text-emerald-700' : 'text-gray-400'}`}>
                        {active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {removing ? (
                      <button
                        onClick={() => toggleRemove(row)}
                        className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                        title="Keep it on this list"
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        onClick={() => toggleRemove(row)}
                        className="p-2 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                        title={`Remove from ${scopeLabel}'s list (stays in the catalogue)`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Unsaved changes bar. Every toggle and removal on this list commits together in one
          transaction, so a half-applied list is not a state you can end up in. */}
      {pendingCount > 0 && (
        <div className="sticky bottom-0 z-30 mt-4 -mx-6 px-6 pb-4">
          <div className="max-w-6xl mx-auto bg-white border border-amber-300 shadow-xl rounded-2xl px-5 py-3 flex items-center gap-4 flex-wrap">
            <span className="flex-1 min-w-[12rem] text-sm text-gray-700">
              <span className="font-semibold text-amber-700">
                {pendingCount} unsaved {pendingCount === 1 ? 'change' : 'changes'}
              </span>
              {(() => {
                const on = [...pendingActive.values()].filter((v) => v === true).length;
                const off = [...pendingActive.values()].filter((v) => v === false).length;
                const bits = [];
                if (on) bits.push(`${on} to activate`);
                // Switching off takes the row off this list (it returns to the catalogue), so the
                // summary says that rather than "switch off", which sounded like it would stay put
                // and merely be greyed out.
                if (off) bits.push(`${off} to remove from this list`);
                if (pendingRemove.size) bits.push(`${pendingRemove.size} to delete`);
                return bits.length ? <span className="text-gray-500"> &middot; {bits.join(', ')}</span> : null;
              })()}
            </span>
            <button
              type="button"
              onClick={discardChanges}
              disabled={savingBatch}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={saveBatch}
              disabled={savingBatch}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {savingBatch ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickLabTestsTab;
